import React, { useState, useCallback, useEffect } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import moment from 'moment';
import { SingleDatePicker } from 'react-dates';
import { useBrsCreateStyles } from '../BrsCreateStyles';
import { IsciStatusBadge } from './IsciStatusBadge';
import { OriginalIsciRow } from './OriginalIsciRow';
import { SplitWeekSchedule } from './SplitWeekSchedule';
import { DAYS } from './splitWeekUtils';
import { hasOriginals, spotIsNew } from '../brsValidation';
import { getSpotMetadataByCopyId } from '../../../helpers';

const ISO_FORMAT = 'YYYY-MM-DD';

const toMoment = (value) => {
  if (!value) return null;
  const m = moment(value, ISO_FORMAT, true);
  return m.isValid() ? m : null;
};

export const IsciEntryCard = ({
  index,
  spot,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  brandChoices,
  unitRotation,
  siblingIscis,
  flightStartDate,
  flightEndDate,
}) => {
  const notify = useNotify();
  const classes = useBrsCreateStyles();
  const dataProvider = useDataProvider();
  const [validatingIsci, setValidatingIsci] = useState(false);
  const [durations, setDurations] = useState({});
  const [splitWeekOpen, setSplitWeekOpen] = useState(false);
  const [startDateFocused, setStartDateFocused] = useState(false);
  const [endDateFocused, setEndDateFocused] = useState(false);

  // Build the isOutsideRange function for constraining to flight window.
  const flightStart = toMoment(flightStartDate);
  const flightEnd = toMoment(flightEndDate);
  const isOutsideFlightRange = (day) => {
    if (!flightStart && !flightEnd) return false;
    if (flightStart && day.isBefore(flightStart, 'day')) return true;
    if (flightEnd && day.isAfter(flightEnd, 'day')) return true;
    return false;
  };

  const totalDuration = Object.values(durations).reduce((acc, d) => acc + (Number(d) || 0), 0);

  const isciExists = spot.isciasset?.exists;

  const entryClass = [
    classes.isciEntry,
    isciExists === true ? classes.isciEntryExisting : '',
    isciExists === false ? classes.isciEntryNew : '',
  ]
    .filter(Boolean)
    .join(' ');

  // Given ISCI start/end dates, compute which day-of-week keys (mon..sun)
  // fall inside the range. Returns a Set of day keys. If either date is
  // missing the set is empty (no constraint).
  const activeDayKeys = (startStr, endStr) => {
    if (!startStr || !endStr) return new Set(DAYS.map((d) => d.key));
    const s = new Date(startStr + 'T00:00:00');
    const e = new Date(endStr + 'T00:00:00');
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return new Set(DAYS.map((d) => d.key));
    const active = new Set();
    const cur = new Date(s);
    while (cur <= e) {
      // JS getDay(): Sun=0..Sat=6 → convert to Mon=0..Sun=6 to match DAYS order
      const jsDay = cur.getDay();
      const idx = jsDay === 0 ? 6 : jsDay - 1;
      active.add(DAYS[idx].key);
      cur.setDate(cur.getDate() + 1);
    }
    return active;
  };

  // When ISCI dates change, zero out days in the split_week_schedule that
  // fall outside the selected sub-range.
  const applyDateRangeToSchedule = (startStr, endStr, currentSpot) => {
    const active = activeDayKeys(startStr, endStr);
    const defaultVal = unitRotation === 'unit' ? 1 : 100;
    const schedule = { ...(currentSpot.split_week_schedule || {}) };
    for (const d of DAYS) {
      if (active.has(d.key)) {
        // If the day was previously zeroed by a range constraint, restore it
        if (schedule[d.key] === 0) schedule[d.key] = defaultVal;
      } else {
        schedule[d.key] = 0;
      }
    }
    return schedule;
  };

  const updateSpot = (updates) => {
    onChange(index, { ...spot, ...updates });
  };

  const updateIsciAsset = useCallback(
    (updates) => {
      onChange(index, {
        ...spot,
        isciasset: { ...spot.isciasset, ...updates },
      });
    },
    [index, spot, onChange],
  );

  // Shared lookup: queries DH for local isciasset metadata AND CatDV for the
  // smart-form scenario. Used by both the manual-blur handler and the
  // mount-time auto-lookup that classifies spots added via Bulk Add /
  // Search Add (which never fire a blur event on the ISCI field).
  //
  // Cadent/CatDV is the authoritative source for the NEW vs EXISTING badge
  // per BRS-FORM-P1: "the system will query CatDV to determine whether that
  // spot has been previously delivered." DH is consulted purely to preserve
  // local encoding/sigma settings when they're already on file.
  const runIsciLookup = useCallback(
    async (rawValue) => {
      if (!rawValue) return;
      const value = rawValue.endsWith('H') ? rawValue : rawValue + 'H';
      if (value !== rawValue) updateIsciAsset({ isci: value });

      setValidatingIsci(true);
      try {
        const [dhResult, spotMetadata] = await Promise.all([
          dataProvider
            .getList('isciasset', {
              filter: { isci: value },
              pagination: { page: 1, perPage: 1 },
              sort: { field: 'isci', order: 'ASC' },
            })
            .catch((err) => {
              console.error('DH isciasset lookup failed:', err);
              return { data: [] };
            }),
          getSpotMetadataByCopyId(value),
        ]);

        const dhAsset = dhResult?.data?.[0];
        const cadentExists = spotMetadata?.exists === true;
        const updatedSpot = { ...spot };

        if (dhAsset) {
          updatedSpot.isciasset = {
            ...dhAsset,
            original_iscis:
              dhAsset.original_iscis?.length
                ? dhAsset.original_iscis
                : spot.isciasset?.original_iscis?.length
                  ? spot.isciasset.original_iscis
                  : [{}],
          };
        } else {
          updatedSpot.isciasset = {
            ...spot.isciasset,
            isci: value,
            original_iscis: spot.isciasset?.original_iscis?.length
              ? spot.isciasset.original_iscis
              : [{}],
          };
        }

        // CatDV is the sole source of truth for NEW/EXISTING classification —
        // never infer EXISTING from a DH-local row (the spot may have been
        // ingested before but that's orthogonal to whether Cadent has it).
        updatedSpot.isciasset.exists = cadentExists;

        if (cadentExists) {
          updatedSpot.traffic_only = true;
          updatedSpot.copy_id = value;
          if (!spot.title && spotMetadata.title) {
            updatedSpot.title = spotMetadata.title;
          }
          const cadentDuration = Number(spotMetadata.length ?? spotMetadata.duration) || 0;
          if (cadentDuration && !updatedSpot.isciasset.duration) {
            updatedSpot.isciasset.duration = cadentDuration;
          }
          if (spotMetadata.brand && !updatedSpot.isciasset.brand) {
            updatedSpot.isciasset.brand = spotMetadata.brand;
          }
        } else {
          updatedSpot.traffic_only = false;
          updatedSpot.copy_id = null;
        }

        // An existing (traffic-only) master normally reloads its original
        // ISCIs from DH and the section renders read-only. But some masters
        // exist in Cadent with no stored originals in DH (nothing came back on
        // the lookup) — locking that empty section is a dead end: the user can
        // neither see autofilled data nor type it in. Flag those so the
        // Original-ISCI section stays editable for manual entry. The flag lives
        // on isciasset and survives field edits, so typing an original ISCI
        // won't flip the section back to locked mid-entry.
        const hasLoadedOriginals = (updatedSpot.isciasset.original_iscis || []).some(
          (o) => o && (o.isci || o.title || o.advertiser_name || o.brand_code || o.brand_name),
        );
        updatedSpot.isciasset.allow_manual_originals = cadentExists && !hasLoadedOriginals;

        onChange(index, updatedSpot);
      } catch (err) {
        console.error('ISCI lookup failed:', err);
        updateIsciAsset({ isci: value, exists: false, original_iscis: [{}] });
      }
      setValidatingIsci(false);
    },
    [dataProvider, index, spot, onChange, updateIsciAsset],
  );

  const handleIsciBlur = useCallback(
    (e) => {
      const raw = (e.target.value || '').trim().toUpperCase();
      const candidate = raw && !raw.endsWith('H') ? `${raw}H` : raw;
      if (candidate && siblingIscis && siblingIscis.has(candidate)) {
        notify(`${candidate} is already in another row`, 'warning');
        updateIsciAsset({ isci: '', exists: undefined });
        return;
      }
      runIsciLookup(e.target.value);
    },
    [runIsciLookup, siblingIscis, notify, updateIsciAsset],
  );

  const handleIsciChange = (value) => {
    updateIsciAsset({ isci: value });
  };

  // Bulk Add and Search Add drop ISCIs straight onto the form without firing
  // a blur on the IsciEntryCard input. Kick off the CatDV lookup once on
  // mount for any spot that arrives with an ISCI but no classification yet,
  // so those entries get the NEW/EXISTING badge and hybrid-mode detection
  // that manually-typed spots get. Pre-populated (traffic update) spots
  // already carry `exists: true` from the pre-populate path, so they skip.
  useEffect(() => {
    const isci = spot.isciasset?.isci;
    const classified = spot.isciasset?.exists !== undefined;
    if (isci && !classified && !spot.isPrePopulated) {
      runIsciLookup(isci);
    }
    // Only on mount — re-running on every spot change would re-trigger the
    // lookup after the first result lands and flip state in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOriginalIsciChange = (origIndex, updatedOrigIsci) => {
    const origIscis = [...(spot.isciasset?.original_iscis || [])];
    origIscis[origIndex] = updatedOrigIsci;
    updateIsciAsset({ original_iscis: origIscis });
  };

  const handleAddOriginalIsci = () => {
    const origIscis = [...(spot.isciasset?.original_iscis || [])];
    if (origIscis.length < 4) {
      origIscis.push({});
      updateIsciAsset({ original_iscis: origIscis });
    }
  };

  const handleRemoveOriginalIsci = (origIndex) => {
    const origIscis = [...(spot.isciasset?.original_iscis || [])];
    origIscis.splice(origIndex, 1);
    const newDurations = {};
    Object.entries(durations).forEach(([key, val]) => {
      const k = Number(key);
      if (k < origIndex) newDurations[k] = val;
      else if (k > origIndex) newDurations[k - 1] = val;
    });
    setDurations(newDurations);
    updateIsciAsset({ original_iscis: origIscis });
  };

  const handleDurationChange = (origIndex, duration) => {
    setDurations((prev) => ({ ...prev, [origIndex]: duration }));
  };

  useEffect(() => {
    if (totalDuration > 0 && spot.isciasset?.duration !== totalDuration) {
      onChange(index, {
        ...spot,
        isciasset: { ...spot.isciasset, duration: totalDuration },
      });
    }
  }, [totalDuration, index, onChange, spot]);

  // An existing master with no stored original ISCIs is flagged for manual
  // entry (see runIsciLookup) — keep its Original-ISCI section editable instead
  // of stranding the user with empty, locked fields they can't fill.
  const allowManualOriginals = spot.isciasset?.allow_manual_originals === true;
  const originalIscisDisabled =
    !allowManualOriginals && (isciExists === true || isciExists === undefined);

  return (
    <div className={entryClass}>
      {/* Top row: number, arrows, dates, title, badge, remove */}
      <div className={classes.isciTopRow}>
        <div className={classes.isciNumber}>{index + 1}</div>
        <div className={classes.isciArrows}>
          <button
            className={classes.isciArrowBtn}
            onClick={() => onMoveUp(index)}
            disabled={isFirst}
            type="button"
          >
            &#9650;
          </button>
          <button
            className={classes.isciArrowBtn}
            onClick={() => onMoveDown(index)}
            disabled={isLast}
            type="button"
          >
            &#9660;
          </button>
        </div>
        <div className={classes.isciDateFields}>
          <div className={classes.formGroup} style={{ minWidth: 140 }}>
            <label className={classes.formLabel}>Start Date</label>
            <div className={classes.flightDatePicker}>
              <SingleDatePicker
                id={`isci_start_date_${spot._id}`}
                date={toMoment(spot.start_date)}
                onDateChange={(m) => {
                  const newStart = m ? m.format(ISO_FORMAT) : '';
                  const newSchedule = applyDateRangeToSchedule(newStart, spot.end_date, spot);
                  updateSpot({ start_date: newStart, split_week_schedule: newSchedule });
                }}
                focused={startDateFocused}
                onFocusChange={({ focused }) => setStartDateFocused(focused)}
                isOutsideRange={isOutsideFlightRange}
                numberOfMonths={1}
                displayFormat={ISO_FORMAT}
                placeholder="Select date"
                noBorder
                block
                small
                hideKeyboardShortcutsPanel
                withPortal
              />
            </div>
          </div>
          <div className={classes.formGroup} style={{ minWidth: 140 }}>
            <label className={classes.formLabel}>End Date</label>
            <div className={classes.flightDatePicker}>
              <SingleDatePicker
                id={`isci_end_date_${spot._id}`}
                date={toMoment(spot.end_date)}
                onDateChange={(m) => {
                  const newEnd = m ? m.format(ISO_FORMAT) : '';
                  const newSchedule = applyDateRangeToSchedule(spot.start_date, newEnd, spot);
                  updateSpot({ end_date: newEnd, split_week_schedule: newSchedule });
                }}
                focused={endDateFocused}
                onFocusChange={({ focused }) => setEndDateFocused(focused)}
                isOutsideRange={isOutsideFlightRange}
                numberOfMonths={1}
                displayFormat={ISO_FORMAT}
                placeholder="Select date"
                noBorder
                block
                small
                hideKeyboardShortcutsPanel
                withPortal
              />
            </div>
          </div>
          <div className={classes.formGroupW2}>
            <label className={classes.formLabel}>Title *</label>
            <input
              className={classes.formInput}
              value={spot.title || ''}
              onChange={(e) => updateSpot({ title: e.target.value })}
              placeholder="Enter title..."
              readOnly={spot.isPrePopulated === true}
              style={spot.isPrePopulated ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
            />
          </div>
        </div>
        <IsciStatusBadge exists={isciExists} />
        <div style={{ flexShrink: 0 }}>
          <button className={classes.btnDangerText} onClick={() => onRemove(index)} type="button">
            &#8854; REMOVE
          </button>
        </div>
      </div>

      {/* Body: ISCI fields */}
      <div className={classes.isciBody}>
        <div className={classes.formRow}>
          <div className={classes.formGroupFixedMd}>
            <label className={classes.formLabel}>ISCI</label>
            <input
              className={classes.formInput}
              value={spot.isciasset?.isci || ''}
              onChange={(e) => handleIsciChange(e.target.value)}
              onBlur={handleIsciBlur}
              placeholder="Enter ISCI..."
            />
            {validatingIsci && <div className={classes.helperText}>Checking ISCI...</div>}
          </div>
          <div className={classes.formGroupFixedSm}>
            <label className={classes.formLabel}>Sigma Encoding *</label>
            <select
              className={classes.formSelect}
              value={spot.isciasset?.sigma_encoding || 'CADENT'}
              onChange={(e) => updateIsciAsset({ sigma_encoding: e.target.value })}
              disabled={isciExists === true}
            >
              <option value="CADENT">Cadent</option>
              <option value="NONE">None</option>
              <option value="TTWN">TTWN</option>
            </select>
          </div>
          <div className={classes.formGroupFixedSm}>
            <label className={classes.formLabel}>BVS Encoding *</label>
            <select
              className={classes.formSelect}
              value={spot.isciasset?.bvs_encoding || 'NO'}
              onChange={(e) => updateIsciAsset({ bvs_encoding: e.target.value })}
              disabled={true}
            >
              <option value="NO">No</option>
              <option value="YES">Yes</option>
            </select>
          </div>
          <div style={{ flex: '0 0 80px' }}>
            <label className={classes.formLabel}>
              {unitRotation === 'rotation' ? 'Rotation' : 'Unit'}
            </label>
            <input
              className={classes.formInput}
              value={spot.unit || spot.rotation || ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (unitRotation === 'rotation') {
                  // Mirror the rotation % to every day of the split-week schedule
                  // so operators don't have to re-enter it in the summary table.
                  // Empty input leaves the schedule alone — clearing the field
                  // should not silently wipe a user-customized per-day split.
                  const updates = { rotation: raw, unit: '' };
                  const n = Number(raw);
                  if (raw !== '' && Number.isFinite(n)) {
                    updates.split_week_schedule = {
                      mon: n, tue: n, wed: n, thu: n, fri: n, sat: n, sun: n,
                    };
                  }
                  updateSpot(updates);
                } else {
                  // Mirror the unit count to every day of the split-week schedule
                  // so operators don't have to re-enter it in the summary table.
                  const updates = { unit: raw, rotation: '' };
                  const n = Number(raw);
                  if (raw !== '' && Number.isFinite(n)) {
                    updates.split_week_schedule = {
                      mon: n, tue: n, wed: n, thu: n, fri: n, sat: n, sun: n,
                    };
                  }
                  updateSpot(updates);
                }
              }}
              placeholder={unitRotation === 'rotation' ? '%' : 'unit'}
            />
          </div>
          <div style={{ flex: '0 0 120px', display: 'flex', alignItems: 'flex-end', paddingBottom: 2 }}>
            <div className={classes.toggleWrap}>
              <div
                className={`${classes.toggle} ${spot.isciasset?.include_sd ? classes.toggleOn : ''}`}
                onClick={() => {
                  if (isciExists !== true) {
                    updateIsciAsset({ include_sd: !spot.isciasset?.include_sd });
                  }
                }}
                style={isciExists === true ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              />
              <span className={classes.toggleText}>Include SD *</span>
            </div>
          </div>
        </div>

        {/* Original ISCIs */}
        {(spot.isciasset?.original_iscis || []).map((origIsci, origIdx) => (
          <div key={origIdx} style={{ position: 'relative' }}>
            <OriginalIsciRow
              index={origIdx}
              data={origIsci}
              onChange={(updated) => handleOriginalIsciChange(origIdx, updated)}
              disabled={originalIscisDisabled}
              isciAssetExists={isciExists === true && !allowManualOriginals}
              isPrePopulated={spot.isPrePopulated === true}
              includeSD={spot.isciasset?.include_sd}
              brandChoices={brandChoices}
              onDurationChange={handleDurationChange}
            />
            {origIdx > 0 && !originalIscisDisabled && (
              <button
                className={classes.btnDangerText}
                style={{ position: 'absolute', top: 8, right: 8, fontSize: 11 }}
                onClick={() => handleRemoveOriginalIsci(origIdx)}
                type="button"
              >
                &#215;
              </button>
            )}
          </div>
        ))}

        {!originalIscisDisabled && (spot.isciasset?.original_iscis?.length || 0) < 4 && (
          <button className={classes.btnAddText} onClick={handleAddOriginalIsci} type="button" style={{ marginTop: 8 }}>
            &#8853; ADD
          </button>
        )}

        {spotIsNew(spot) && !hasOriginals(spot) && (
          <div className={classes.fieldError}>
            At least one Original ISCI (DH Master) is required
          </div>
        )}
      </div>

      {/* Total Duration */}
      <div className={classes.totalDuration}>
        Total Duration: {isciExists === true ? spot.isciasset?.duration || 0 : totalDuration}
      </div>

      {/* Split Week Schedule - collapsible */}
      <div className={classes.splitWeekToggle} onClick={() => setSplitWeekOpen(!splitWeekOpen)}>
        <span className={`${classes.splitWeekArrow} ${splitWeekOpen ? classes.splitWeekArrowOpen : ''}`}>
          &#9654;
        </span>
        <span className={classes.splitWeekLabel}>Split Week Schedule</span>
        <span className={classes.splitWeekHint}>
          {splitWeekOpen ? '' : 'Click to set day-by-day rotation'}
        </span>
      </div>
      <div className={`${classes.splitWeekPanel} ${splitWeekOpen ? classes.splitWeekPanelOpen : ''}`}>
        <div className={classes.splitWeekInner}>
          <SplitWeekSchedule
            spot={spot}
            onChange={(updated) => onChange(index, updated)}
            unitRotation={unitRotation}
          />
        </div>
      </div>
    </div>
  );
};
